<template>
  <div
    v-if="visible && progress"
    data-cy="challenge-panel"
    class="tabletLandscape:bottom-5 fixed bottom-20 left-1/2 z-50 -translate-x-1/2"
  >
    <!-- Collapsed pill — desktop only; on mobile the BottomMenuBar slot opens it. -->
    <button
      v-if="!expanded"
      type="button"
      data-cy="challenge-panel-pill"
      class="shadow-bottom-menu tabletLandscape:flex hidden items-center gap-2.5 rounded-full bg-white py-2.5 pr-5 pl-2.5 backdrop-blur-md"
      @click="toggle"
    >
      <span
        class="flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
        :class="isComplete ? 'color-variant-info' : 'color-variant-most-important'"
      >
        <GenericIcon v-if="isComplete" icon="icon-[mingcute--check-fill]" icon-size="size-4.5" />
        <template v-else>{{ completedCount }}/{{ totalCount }}</template>
      </span>
      <span class="text-tm-gray-900 text-sm font-bold">
        {{ progress.challengeTitle }}
      </span>
      <span
        v-if="isComplete"
        data-cy="challenge-panel-complete"
        class="text-tm-petrol-base text-xs font-semibold"
      >
        Geschafft!
      </span>
    </button>

    <!-- Expanded checklist — shared by desktop pill and mobile nav slot. -->
    <div
      v-if="expanded"
      data-cy="challenge-panel-expanded"
      class="shadow-bottom-menu w-80 max-w-[calc(100vw-2.5rem)] rounded-2xl bg-white p-5 backdrop-blur-md"
    >
      <div class="mb-2.5 flex items-center justify-between gap-2.5">
        <SidebarSectionHeader :heading="progress.challengeTitle" />
        <div class="flex shrink-0 items-center gap-2.5">
          <span
            v-if="isComplete"
            data-cy="challenge-panel-complete"
            class="text-tm-petrol-base text-xs font-semibold"
          >
            Geschafft!
          </span>
          <GenericButton
            data-cy="challenge-panel-collapse"
            icon="icon-[mingcute--close-line]"
            aria-label="Einklappen"
            :color-variant="ColorVariants.EXTRA"
            @click="close"
          />
        </div>
      </div>

      <ol class="flex flex-col gap-2.5">
        <li
          v-for="(task, idx) in progress.tasks"
          :key="task.taskId"
          :data-cy="`challenge-task-${task.taskId}`"
          :data-state="task.state"
          class="flex items-start gap-2.5 rounded-xl p-2.5"
          :class="{
            'color-variant-active': task.state === 'ACTIVE',
            'color-variant-disabled bg-white': task.state === 'PENDING',
          }"
        >
          <span
            class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
            :class="badgeClass(task.state)"
          >
            <GenericIcon
              v-if="task.state === 'COMPLETED'"
              icon="icon-[mingcute--check-fill]"
              icon-size="size-3"
            />
            <template v-else>{{ idx + 1 }}</template>
          </span>
          <p
            class="text-sm leading-snug"
            :class="{ 'line-through opacity-70': task.state === 'COMPLETED' }"
          >
            {{ task.goal }}
          </p>
        </li>
      </ol>

      <div
        data-cy="challenge-clock-controls"
        class="border-tm-gray-100 mt-2.5 flex flex-col gap-2.5 rounded-xl border p-2.5"
      >
        <SidebarSectionHeader heading="Simulierte Zeit">
          {{ simulatedTimeLabel }}
        </SidebarSectionHeader>
        <div class="flex flex-wrap gap-2.5">
          <GenericButton
            v-for="step in advanceSteps"
            :key="step.label"
            :data-cy="step.dataCy"
            :disabled="advancing"
            :text="step.label"
            :color-variant="ColorVariants.ALTERNATIVE"
            btn-class="min-h-0! flex-1 px-2.5! py-2"
            @click="advance(step.deltaMs)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SidebarSectionHeader from '@/components/Generic/SidebarSectionHeader.vue';
import { useChallengePanel } from '@/composables/useChallengePanel';
import {
  bumpChallengeClockOffsetMsOptimistic,
  setChallengeClockOffsetMs,
  useSystemTime,
} from '@therame/ui';
import { challengeMutations } from '@therame/api-client';
import { ColorVariants } from '@therame/design-tokens/src/color-variants';
import { ChallengeTaskState } from '@therame/shared';
import { GenericButton, GenericIcon, GenericInfoBanner } from '@therame/ui';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { computed, ref, watch } from 'vue';

const { expanded, progress, visible, totalCount, completedCount, isComplete, toggle, close } =
  useChallengePanel();

// One-time nudge: the collapsed pill (desktop) / bottom-nav trophy (mobile) isn't
// obviously a task list, so on first appearance we highlight it with driver.js.
const HINT_STORAGE_KEY = 'challenge_tasklist_hint_shown';
const isTestMode = (import.meta.env.VITE_MODE ?? '').startsWith('TEST');

const waitForVisibleTarget = async (
  selectors: string[],
  maxRetries = 20,
  delayMs = 100,
): Promise<HTMLElement | null> => {
  for (let i = 0; i < maxRetries; i++) {
    for (const sel of selectors) {
      const el = document.querySelector<HTMLElement>(sel);
      // offsetParent is null when the element is display:none (the hidden surface
      // for the current breakpoint), so this picks whichever pill/toggle is shown.
      if (el && el.offsetParent !== null) return el;
    }
    await new Promise((res) => setTimeout(res, delayMs));
  }
  return null;
};

const showTaskListHint = async (): Promise<void> => {
  if (isTestMode) return;
  if (window.localStorage.getItem(HINT_STORAGE_KEY) === 'true') return;

  const target = await waitForVisibleTarget([
    '[data-cy="challenge-panel-pill"]',
    '[data-cy="challenge-nav-toggle"]',
  ]);
  if (!target) return;

  window.localStorage.setItem(HINT_STORAGE_KEY, 'true');

  const driverObj = driver({
    allowClose: true,
    overlayClickBehavior: 'close',
    showButtons: ['close'],
  });

  // driver.js leaves the highlighted element clickable but won't dismiss on its
  // click — clicking the pill expands the panel, which makes the hint moot, so we
  // tear the highlight down ourselves.
  const dismissOnClick = (): void => driverObj.destroy();
  target.addEventListener('click', dismissOnClick, { once: true });

  driverObj.highlight({
    element: target,
    popover: {
      title: 'Deine Aufgabenliste',
      description:
        'Hier verfolgst du deine Challenge-Aufgaben. Klicke darauf, um alle Schritte und deinen Fortschritt zu sehen.',
    },
    onDeselected: () => target.removeEventListener('click', dismissOnClick),
  });
};

const stopHintWatch = watch(
  [visible, progress, expanded],
  ([isVisible, prog, isExpanded]) => {
    // Only nudge while the panel is collapsed — once it's open the hint is moot.
    if (!isVisible || !prog || isExpanded) return;
    stopHintWatch();
    void showTaskListHint();
  },
  { immediate: true },
);

const { nowDayjs, offsetMs } = useSystemTime();

const badgeClass = (state: ChallengeTaskState): string => {
  if (state === ChallengeTaskState.COMPLETED) return 'color-variant-most-important';
  if (state === ChallengeTaskState.ACTIVE) return 'color-variant-most-important';
  return 'color-variant-inactive';
};

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

const advanceSteps = [
  { label: '+1 Stunde', deltaMs: HOUR_MS, dataCy: 'challenge-clock-advance-hour' },
  { label: '+1 Tag', deltaMs: DAY_MS, dataCy: 'challenge-clock-advance-day' },
  { label: '+1 Woche', deltaMs: WEEK_MS, dataCy: 'challenge-clock-advance-week' },
] as const;

const simulatedTimeLabel = computed(() => nowDayjs().locale('de').format('dd, DD.MM.YYYY HH:mm'));

const advancing = ref(false);

// Optimistic-bump-then-fire: we read the post-bump offset back from useSystemTime
// (the single source of truth on the FE), compute the absolute target from it,
// and roll back if the server rejects. Forward-only is enforced server-side; a
// 400 here means a concurrent advance from another tab already moved past us,
// and the next /challenge/progress poll will reconcile.
const advance = async (deltaMs: number): Promise<void> => {
  if (advancing.value) return;
  advancing.value = true;
  const preBumpOffsetMs = offsetMs.value;
  bumpChallengeClockOffsetMsOptimistic(deltaMs);
  const targetOffsetMs = preBumpOffsetMs + deltaMs;
  try {
    const res = await challengeMutations.advanceClock(targetOffsetMs);
    setChallengeClockOffsetMs(res.clockOffsetMs);
  } catch {
    setChallengeClockOffsetMs(preBumpOffsetMs);
  } finally {
    advancing.value = false;
  }
};
</script>
